import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RepeatMonthlyStrategy } from '../../types';
import { Weekday } from 'src/@types/weekday';
import { DateHelper } from 'src/shared/helpers/date';
import { useGetWeekdayPositionInMonth } from 'src/shared/hooks/useGetWeekdayPositionInMonth';
export const useCustomFrequencyDescription = ({ date, every, repeatWeekConfig, repeatMonthConfig }) => {
    const { t } = useTranslation();
    const { position } = useGetWeekdayPositionInMonth(date);
    const weekdayText = useMemo(() => ({
        [Weekday.Sunday]: t('mentorship.abbreviated_weekday.sunday'),
        [Weekday.Monday]: t('mentorship.abbreviated_weekday.monday'),
        [Weekday.Tuesday]: t('mentorship.abbreviated_weekday.tuesday'),
        [Weekday.Wednesday]: t('mentorship.abbreviated_weekday.wednesday'),
        [Weekday.Thursday]: t('mentorship.abbreviated_weekday.thursday'),
        [Weekday.Friday]: t('mentorship.abbreviated_weekday.friday'),
        [Weekday.Saturday]: t('mentorship.abbreviated_weekday.saturday')
    }), [t]);
    const getDailyDescription = useCallback(() => {
        if (every === 1) {
            return t('content_list.module_card.recurrence_description.daily');
        }
        return t('content_list.module_card.recurrence_description.every_x_days', { every });
    }, [t, every]);
    const getWeeklyDescription = useCallback(() => {
        const weekdays = Object.entries(repeatWeekConfig ?? {})
            .filter(([_, value]) => value)
            .map(([key]) => weekdayText[key])
            .join(', ');
        if (every === 1) {
            return `${t('content_list.module_card.recurrence_description.weekly')} ${weekdays}`;
        }
        return `${t('content_list.module_card.recurrence_description.every_x_weeks', { every })} ${weekdays}`;
    }, [t, every, repeatWeekConfig, weekdayText]);
    const getMonthlyDescription = useCallback(() => {
        const weekday = DateHelper.formatIsoString(date.toISOString(), { weekday: 'long' });
        let prefix = t('content_list.module_card.recurrence_description.monthly');
        if (every > 1) {
            prefix = t('content_list.module_card.recurrence_description.every_x_months', { every });
        }
        if (repeatMonthConfig?.day) {
            return `${prefix} ${t('content_list.module_card.recurrence_description.on_the_day', { date: date.getDate() }).toLowerCase()}`;
        }
        if (repeatMonthConfig?.dayOfTheWeekStrategy == RepeatMonthlyStrategy.OnLastWeekday) {
            return `${prefix} ${t('content_list.module_card.recurrence_description.on').toLowerCase()} ${t('mentorship.day_position_in_month.last')} ${weekday}`;
        }
        return `${prefix} ${t('content_list.module_card.recurrence_description.on').toLowerCase()} ${position.text} ${weekday}`;
    }, [t, every, date, position, repeatMonthConfig]);
    return {
        getDailyDescription,
        getWeeklyDescription,
        getMonthlyDescription
    };
};
